import { parsePhoneNumberFromString } from 'libphonenumber-js'
import dayjs from 'dayjs'
import numeral from 'numeral'

function withValueCheck(value, formatter) {
  // TODO: expand this to handle more types of values
  return value !== undefined && value !== null ? formatter(value) : ''
}

export function commaDelimitedNumber(value) {
  return withValueCheck(value, val => numeral(val).format('0,0'))
}

export function currency(value, withCents = false) {
  // withCents truthy: forces display of cents even if zero
  // withCents falsy: only displays cents if non-zero
  return withValueCheck(value, val => numeral(val).format(withCents ? '$0,0.00' : '$0,0[.]00'))
}

export function currencyWithCents(value) {
  return withValueCheck(value, val => currency(val, true))
}

export function currencyNoCents(value) {
  return withValueCheck(value, val => currency(val, false))
}

export function percent(value) {
  return withValueCheck(value, val => numeral(val).format('0,0[.]00%'))
}

export function dateAtTime(value, options = {}) {
  const { compact = false } = options
  const format = compact ? 'MMM D, YYYY h:mm A' : 'MMMM D, YYYY h:mm A'
  return withValueCheck(value, val => dayjs(val).format(format))
}

export function dateAtTimeWithSeconds(value, options = {}) {
  const { compact = false } = options
  const format = compact ? 'M/DD/YY h:mm:ssa' : 'MM/DD/YYYY h:mm:ss A'
  return withValueCheck(value, val => dayjs(val).format(format))
}

export function dateLong(value) {
  return withValueCheck(value, val => dayjs(val).format('MMMM D, YYYY'))
}

export function dateMDY(value, formatString = 'MM/DD/YYYY') {
  return withValueCheck(value, val => dayjs(val).format(formatString))
}

export function dateMDYWithTime(value) {
  return dateMDY(value, 'MM/DD/YYYY, h:mm A')
}

export function dateYMD(value) {
  return withValueCheck(value, val => dayjs(val).format('YYYY-MM-DD'))
}

export function dateMDYShort(value, formatString = 'M/D/YY') {
  return withValueCheck(value, val => dayjs(val).format(formatString))
}

export function dateMDYShortWithTime(value) {
  return dateMDYShort(value, 'M/D/YY, h:mm A')
}

export function dateMY(value) {
  return withValueCheck(value, val => dayjs(val).format('MM/YYYY'))
}

export function dateYM(value) {
  return withValueCheck(value, val => dayjs(val).format('YYYY-MM'))
}

export function dateMMMD(value, { withTime = false } = {}) {
  const format = withTime ? 'MMM D, h:mm A' : 'MMM D'
  return withValueCheck(value, val => dayjs(val).format(format))
}

export function fileDateTime(value) {
  return withValueCheck(value, val => dayjs(val).format('YYYYMMDD_HHmmss'))
}

export function pluralizeWord(singular, count) {
  return count === 1 ? singular : `${singular}s`
}

export function maskEmail(email) {
  return withValueCheck(email, val => {
    const [username, domain] = val.split('@')
    const redactedUsername =
      username.length > 4
        ? `${username.slice(0, 2)}******${username.slice(-2)}`
        : `${username.slice(0, 1)}******${username.slice(-1)}`
    return `${redactedUsername}@${domain}`
  })
}

/**
 * @function itemsToSentence
 * @description Convert a list of items into part of an english sentence. For example: ['a', 'b', 'c'] => 'a, b, and c'
 * @param {Array} items
 * @returns {String}
 */
export function itemsToSentence(items) {
  return withValueCheck(items, val => {
    if (val.length === 1) {
      return `${val[0]}`
    }
    if (val.length === 2) {
      return `${val[0]} and ${val[1]}`
    }
    return `${val.slice(0, -1).join(', ')}, and ${val.slice(-1)}`
  })
}

/**
 * Convert phone number to E.164 format
 * @param {string} phoneNumber - The phone number to format
 * @param {string} countryCode - The country code (e.g. 'US')
 * @returns {string} - The formatted phone number in E.164 format, or empty string if invalid
 */
export function phoneNumberE164(phoneNumber, countryCode = 'US') {
  return withValueCheck(phoneNumber, val => {
    let resp = ''
    const parsedPhoneNumber = parsePhoneNumberFromString(val, countryCode)
    if (parsedPhoneNumber) {
      resp = parsedPhoneNumber.format('E.164')
    }

    return resp
  })
}
/**
 * Convert phone number to international format
 * @param {string} phoneNumber - The phone number to format
 * @param {string} countryCode - The country code (e.g. 'US')
 * @returns {string} - The formatted phone number in international format, or empty string if invalid
 */
export function phoneNumberNational(phoneNumber, countryCode = 'US') {
  return withValueCheck(phoneNumber, val => {
    let resp = ''
    const parsedPhoneNumber = parsePhoneNumberFromString(val, countryCode)
    if (parsedPhoneNumber && parsedPhoneNumber.isValid()) {
      resp = parsedPhoneNumber.formatNational()
    }

    return resp
  })
}

/**
 * Ensure URL starts with https://
 * @param {string} url - The URL to format
 * @returns {string} - The formatted URL starting with https://
 */
export function httpsUrl(url) {
  return withValueCheck(url, val => {
    // ensure website starts with https://
    if (!val.startsWith('https://')) {
      val = `https://${val.replace(/^https?:\/\//, '')}`
    }

    return val
  })
}

/**
 * @description Format file size in bytes to human readable format
 * @function formatFileSize
 * @param {Number} bytes - File size in bytes
 * @returns {String} Formatted file size (e.g., "1.5 MB", "256 KB")
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * @description Capitalize the first letter of each word in a string
 * @function capitalizeWords
 * @param {String} str - The string to capitalize
 * @param {String|Array} separators - The separator(s) to use for splitting words (default: space)
 * @returns {String} The capitalized string
 */
export function capitalizeWords(str, separators = ' ') {
  // Check if str is a string
  if (typeof str !== 'string') {
    return null
  }

  // Convert separators to an array if it's a string
  const sepArray = typeof separators === 'string' ? [separators] : separators

  // Create a regex pattern from the separators
  const pattern = sepArray.map(sep => sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
  const regex = new RegExp(pattern)

  // Split by any of the separators, capitalize, and rejoin with space
  return str
    .split(regex)
    .map(word => {
      if (word.length === 0) return word
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}
